import apiService from './api';
import type { ApiResponse } from '../types/api';
import type {
  FertiliserPlan, FertiliserProduct, FertiliserProductInput, FertiliserStock, FertRateUnit, JobProducts,
  NutrientMap, PlanLineInput, PlanList, StoreLocation,
} from '../types/fertiliser';

const BASE = '/orchard/fertiliser';

class FertiliserService {
  async products(includeInactive = false): Promise<ApiResponse<FertiliserProduct[]>> {
    return apiService.get(`${BASE}/products${includeInactive ? '?include_inactive=true' : ''}`);
  }

  async createProduct(data: FertiliserProductInput): Promise<ApiResponse<FertiliserProduct>> {
    return apiService.post(`${BASE}/products`, data);
  }

  async updateProduct(id: number, data: FertiliserProductInput): Promise<ApiResponse<FertiliserProduct>> {
    return apiService.put(`${BASE}/products/${id}`, data);
  }

  async stock(): Promise<ApiResponse<FertiliserStock>> {
    return apiService.get(`${BASE}/stock`);
  }

  async recordPurchase(data: { chemical_id: number; location_id: number; quantity: number; unit: string; supplier?: string | null; purchase_date?: string | null; notes?: string | null }): Promise<ApiResponse<unknown>> {
    return apiService.post(`${BASE}/stock/purchases`, data);
  }

  async recordCount(data: { chemical_id: number; location_id: number; counted_quantity: number; unit: string; notes?: string | null }): Promise<ApiResponse<unknown>> {
    return apiService.post(`${BASE}/stock/counts`, data);
  }

  async createLocation(name: string): Promise<ApiResponse<StoreLocation>> {
    return apiService.post(`${BASE}/stock/locations`, { name });
  }

  async plans(season?: number): Promise<ApiResponse<PlanList>> {
    return apiService.get(`${BASE}/plans${season ? `?season=${season}` : ''}`);
  }

  async plan(id: number): Promise<ApiResponse<FertiliserPlan>> {
    return apiService.get(`${BASE}/plans/${id}`);
  }

  async planForArea(areaId: number): Promise<ApiResponse<{ season_year: number; plan: FertiliserPlan | null }>> {
    return apiService.get(`${BASE}/cultivar-areas/${areaId}/plan`);
  }

  async createPlan(data: { cultivar_area_id: number; season_year?: number; targets?: NutrientMap }): Promise<ApiResponse<FertiliserPlan>> {
    return apiService.post(`${BASE}/plans`, data);
  }

  async savePlan(id: number, data: { targets: NutrientMap; notes: string | null; lines: PlanLineInput[] }): Promise<ApiResponse<FertiliserPlan>> {
    return apiService.put(`${BASE}/plans/${id}`, data);
  }

  async deletePlan(id: number): Promise<ApiResponse<void>> {
    return apiService.delete(`${BASE}/plans/${id}`);
  }

  /** Make the farm job that applies a plan line (Farm Jobs creator or admin). */
  async makeJob(lineId: number): Promise<ApiResponse<{ job_id: number; plan: FertiliserPlan | null }>> {
    return apiService.post(`${BASE}/plan-lines/${lineId}/job`, {});
  }

  async jobProducts(jobId: number): Promise<ApiResponse<JobProducts>> {
    return apiService.get(`${BASE}/jobs/${jobId}/products`);
  }

  async addJobProduct(jobId: number, data: { chemical_id: number; rate: number; rate_unit: FertRateUnit }): Promise<ApiResponse<JobProducts>> {
    return apiService.post(`${BASE}/jobs/${jobId}/products`, data);
  }

  async removeJobProduct(id: number): Promise<ApiResponse<JobProducts>> {
    return apiService.delete(`${BASE}/job-products/${id}`);
  }

  async recordUsage(id: number, data: { used_quantity: number; used_unit: string; location_id: number }): Promise<ApiResponse<JobProducts>> {
    return apiService.post(`${BASE}/job-products/${id}/usage`, data);
  }
}

export default new FertiliserService();
